import {getPeakLoadWindows, usePeakLoadWindowsQuery} from '@domain/electricity'
import {useElectricityQuery} from '@domain/schedule'
import {dataTestId} from '@hconnect/uikit'
import {useUpmPlantContext, useUpmPlantTimezone} from '@hconnect/upm'
import {Grid} from '@hm/fundamental'
import {Collapse, Stack} from '@mui/material'
import {
  ECChartLegend,
  ElectricityChart,
  EnergyProviderButton,
  PeakLoadWindowAlert
} from '@widgets/energy-consumption-chart'
import {
  usePlanningChartStartEnd,
  getLocalizedDayMonthTimeFormat,
  useCurrentTime
} from '@widgets/planning-schedule-chart'
import moment, {Moment} from 'moment-timezone'
import React, {useMemo, useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {CollapsibleCard} from '../components/CollapsibleCard'
import {FEATURE_NAMESPACE, OperatorViewLSKeys} from '../consts'
import {useLocalStorageSettings} from '../hooks'

interface OperatorElectricityChartCardProps {
  startOfPlan: Moment
  endOfPlan: Moment
  hoursList: Moment[]
  cellWidth: number
  cellsDisplayed: number
  labelColumnWidth: number
}

const _OperatorElectricityChartCard: React.FC<OperatorElectricityChartCardProps> = ({
  startOfPlan,
  endOfPlan,
  hoursList,
  cellWidth,
  labelColumnWidth,
  cellsDisplayed
}) => {
  const {
    t,
    i18n: {language}
  } = useTranslation(FEATURE_NAMESPACE)
  const timezoneId = useUpmPlantTimezone()
  const {plantId: plantCode} = useUpmPlantContext()
  const now = useCurrentTime({timezoneId})
  const chartWidth = cellWidth * cellsDisplayed
  const {visibleHoursList} = usePlanningChartStartEnd()
  const {data: electricity} = useElectricityQuery({
    plantId: plantCode,
    timezoneId,
    timeFrame: [startOfPlan, endOfPlan]
  })

  const {data: peakLoadWindows} = usePeakLoadWindowsQuery({
    plantId: plantCode,
    timeFrame: [startOfPlan, endOfPlan]
  })

  const powerConsumptionQuarterly = electricity?.planned

  const peakLoadWindowsData = useMemo(
    () =>
      powerConsumptionQuarterly && peakLoadWindows
        ? getPeakLoadWindows({
            peakLoadWindows,
            powerConsumption: powerConsumptionQuarterly
          })
        : undefined,
    [peakLoadWindows, powerConsumptionQuarterly]
  )

  const [minValue, maxValue] = useMemo(() => {
    const powerValues = powerConsumptionQuarterly?.map((item) => item.power)
    return powerValues ? [Math.min(...powerValues), Math.max(...powerValues)] : [0, 1]
  }, [powerConsumptionQuarterly])

  const {settings: chartSettings, updateSettings: setChartSettings} = useLocalStorageSettings(
    OperatorViewLSKeys.ElectricityChartSettings,
    {isElectricityChartOpen: true}
  )
  const isElectricityChartOpen = chartSettings.isElectricityChartOpen

  const toggleIsElectricityChartMinimized = useCallback(
    () => setChartSettings({isElectricityChartOpen: !isElectricityChartOpen}),
    [setChartSettings, isElectricityChartOpen]
  )

  const electricityChartHeight = maxValue > 2 ? 200 : 100

  const electricitySubmittedAt = electricity?.lastSubmitted?.submittedAt
  const electricitySubmittedBy = electricity?.lastSubmitted?.submittedBy

  return (
    <CollapsibleCard
      isCollapsed={!isElectricityChartOpen}
      onToggleCollapsed={toggleIsElectricityChartMinimized}
      title={t('planning.mwPlanned')}
      subtitle={
        electricitySubmittedAt &&
        electricitySubmittedBy &&
        t('planning.lastSubmitted', {
          date: getLocalizedDayMonthTimeFormat(
            moment.utc(electricitySubmittedAt),
            timezoneId,
            language
          ),
          name: electricitySubmittedBy
        })
      }
      headerContent={
        <Grid size={1} className="flex justify-end">
          <EnergyProviderButton startOfPlan={startOfPlan} endOfPlan={endOfPlan} />
        </Grid>
      }
      {...dataTestId('operator_electricity_chart_card')}
    >
      <Collapse in={isElectricityChartOpen}>
        <Stack sx={{position: 'relative'}} spacing={2}>
          <PeakLoadWindowAlert
            peakLoadWindows={peakLoadWindows}
            startOfPlan={startOfPlan}
            endOfPlan={endOfPlan}
          />
          <ECChartLegend showPurchasedInfo={false} />
          <ElectricityChart
            hoursList={hoursList}
            visibleHoursList={visibleHoursList}
            now={now}
            cellWidth={cellWidth}
            yAxisWidth={labelColumnWidth}
            visibleChartWidth={chartWidth}
            height={electricityChartHeight}
            powerQuarterly={powerConsumptionQuarterly}
            energyPurchased={undefined}
            energyUpForPurchase={undefined}
            maxValue={maxValue}
            minValue={minValue}
            peakLoadWindows={peakLoadWindowsData}
            actualPricesEnd={undefined}
            hourlyDiff={undefined}
            isMinimized={false}
          />
        </Stack>
      </Collapse>
    </CollapsibleCard>
  )
}

export const OperatorElectricityChartCard = React.memo(_OperatorElectricityChartCard)
